import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Film, Play, Lock, Loader2, Eye, CalendarDays, Clock, History } from "lucide-react";
import { endpoints } from "@/lib/api";
import { AdSlot } from "@/components/AdSlot";

const RANGES = [
  { id: "7d", label: "Last 7 days", icon: Clock },
  { id: "day", label: "Select date", icon: CalendarDays },
  { id: "older", label: "Older content", icon: History },
];

export default function CloudVideos() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [range, setRange] = useState("7d");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (category = cat, rng = range, d = date) => {
    setLoading(true);
    endpoints.vodBrowse(category, rng, rng === "day" ? d : "")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    endpoints.vodBrowseCategories().then((r) => setCats((r.data?.categories || []).map((c) => typeof c === "string" ? { name: c, display: c } : c))).catch(() => {});
    load("", "7d", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickCat = (c) => { setCat(c); load(c, range, date); };
  const pickRange = (rid) => { setRange(rid); if (rid !== "day") load(cat, rid, ""); };
  const pickDate = (d) => { setDate(d); if (d) load(cat, "day", d); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="videos-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-white flex items-center gap-3 mb-2"><Film className="w-9 h-9 text-[#E11D2A]" /> Videos</h1>
      <p className="text-slate-400 mb-6">Shows, programs and exclusive content — browse by category and date.</p>

      <AdSlot placement="vod" position="top" className="mb-6" />

      {/* Categories */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="vod-categories">
        <button onClick={() => pickCat("")} data-testid="vod-cat-all" className={`h-9 px-4 rounded-full text-sm font-semibold ${!cat ? "bg-[#E11D2A] text-white" : "bg-[#121620] border border-[#273042] text-slate-300"}`}>All</button>
        {cats.map((c) => (
          <button key={c.name} onClick={() => pickCat(c.name)} data-testid={`vod-cat-${c.name}`}
            className={`h-9 px-4 rounded-full text-sm font-semibold ${cat === c.name ? "bg-[#E11D2A] text-white" : "bg-[#121620] border border-[#273042] text-slate-300"}`}>{c.display || c.name}</button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex gap-2 mb-7 flex-wrap items-center" data-testid="vod-ranges">
        {RANGES.map((r) => {
          const Icon = r.icon; const on = range === r.id;
          return (
            <button key={r.id} onClick={() => pickRange(r.id)} data-testid={`vod-range-${r.id}`}
              className={`h-9 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 ${on ? "bg-[#2563EB] text-white" : "bg-[#121620] border border-[#273042] text-slate-300"}`}>
              <Icon className="w-4 h-4" /> {r.label}
            </button>
          );
        })}
        {range === "day" && (
          <input type="date" value={date} onChange={(e) => pickDate(e.target.value)} data-testid="vod-date-input"
            className="h-9 px-3 rounded-full bg-[#0A0C10] border border-[#273042] text-white text-sm outline-none focus:border-[#2563EB]" />
        )}
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>
        : items.length === 0 ? (
          <div className="text-center py-16" data-testid="videos-empty">
            <Film className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-300 font-semibold">No new content in the last 7 days.</p>
            <p className="text-slate-500 text-sm mt-1">Select another date to view previous programs.</p>
            <div className="flex gap-2 justify-center mt-5">
              <button onClick={() => pickRange("day")} data-testid="empty-select-date" className="h-10 px-5 rounded-full bg-[#2563EB] text-white text-sm font-bold flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Select date</button>
              <button onClick={() => pickRange("older")} data-testid="empty-view-older" className="h-10 px-5 rounded-full bg-[#121620] border border-[#273042] text-slate-200 text-sm font-bold flex items-center gap-1.5"><History className="w-4 h-4" /> View older content</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((v) => (
              <Link key={`${v.kind}-${v.id}`} to={v.href} data-testid={`vod-card-${v.id}`} className="group rounded-xl overflow-hidden bg-[#121620] border border-[#273042] hover:border-[#E11D2A]/60 transition-colors">
                <div className="relative aspect-video bg-black">
                  {v.thumbnail ? <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Film className="w-7 h-7 text-slate-700" /></div>}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-12 h-12 rounded-full bg-[#E11D2A] flex items-center justify-center"><Play className="w-5 h-5 text-white ml-0.5" /></span>
                  </div>
                  {v.locked && <span className="absolute top-2 right-2 bg-black/70 text-amber-300 text-[11px] px-2 py-1 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> {v.access === "premium" ? "Premium" : "Subscriber"}</span>}
                  <span className="absolute top-2 left-2 bg-black/60 text-slate-200 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide">{v.kind === "show" ? "Show" : "Video"}</span>
                </div>
                <div className="p-3">
                  <h3 className="text-white text-sm font-semibold line-clamp-2">{v.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{v.category || "General"} · <Eye className="w-3 h-3 inline" /> {v.views || 0}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      <AdSlot placement="vod" position="bottom" className="mt-8" />
    </div>
  );
}
